// Add Friends — three-method add flow per design canon AddFriendsScreen.
// Tabs: QR / LINK / NAME. Pulls the user's friend code from /api/friends/code,
// renders a real (not fake) QR using qrcode-generator. NAME tab debounces a
// /api/friends/search and offers a one-tap [+ add] that POSTs to
// /api/friends/requests. A REDEEM strip at the bottom of NAME accepts a
// 6-letter code typed/pasted by hand. SCAN A CODE uses the camera via a
// hidden file input + jsqr (dynamically imported) — falls back gracefully
// when the platform refuses the capture intent.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import qrcode from 'qrcode-generator'
import { type CSSProperties, type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  type CurrentUser,
  StumbleApiError,
  type UserSearchHit,
  apiFetch,
  authApi,
  friendsApi,
} from '../../api'
import { Icon } from '../../components/Icon'
import { BackButton, TopBar } from '../../components/TopBar'

export const Route = createFileRoute('/your-people/add')({
  component: AddFriendsRoute,
})

type Tab = 'qr' | 'link' | 'name'

const TABS: { id: Tab; label: string; icon: 'spark' | 'share' | 'users' }[] = [
  { id: 'qr', label: 'QR', icon: 'spark' },
  { id: 'link', label: 'Link', icon: 'share' },
  { id: 'name', label: 'Name', icon: 'users' },
]

// Public invite host. Kept in sync with the staging deploy hostname (the link
// itself works as a vanity redirect even before /f/:code is wired server-side
// — the server already accepts the embedded code via redeem).
const INVITE_HOST = 'stmbl.party'

type Toast = { kind: 'success'; text: string } | { kind: 'error'; text: string } | null

function AddFriendsRoute() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('qr')
  const [me, setMe] = useState<CurrentUser | null>(null)
  const [friendCode, setFriendCode] = useState<string | null>(null)
  const [bootError, setBootError] = useState<string | null>(null)
  const [toast, setToast] = useState<Toast>(null)

  useEffect(() => {
    let cancelled = false
    async function boot() {
      const user = await authApi.me()
      if (cancelled) return
      if (!user) {
        navigate({ to: '/login' })
        return
      }
      setMe(user)
      // Friend code comes from /api/friends/code — never trust the cached
      // copy on the user record alone, since the canonical generator lives
      // server-side and may have been backfilled.
      try {
        const res = await apiFetch<{ code: string }>('/api/friends/code')
        if (!cancelled) setFriendCode(res.code)
      } catch (err) {
        if (!cancelled) {
          if (err instanceof StumbleApiError && err.status === 401) {
            navigate({ to: '/login' })
          } else {
            setBootError('Could not load your friend code. Try again.')
          }
        }
      }
    }
    void boot()
    return () => {
      cancelled = true
    }
  }, [navigate])

  // Auto-dismiss toasts so they don't linger.
  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 2400)
    return () => window.clearTimeout(t)
  }, [toast])

  const inviteUrl = useMemo(
    () => (friendCode ? `${INVITE_HOST}/f/${friendCode}` : ''),
    [friendCode],
  )

  function showToast(t: Toast) {
    setToast(t)
  }

  return (
    <div className="stumble-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/your-people' })} />}
        eyebrow="three ways"
        title="add friends"
      />
      {/* F6: explicit height so the 1fr tab-panel slot can fill the viewport.
          Without this, the grid is implicitly sized by its children and the
          tab panel cannot stretch — leaving the yellow blank below content. */}
      <div
        className="screen-body with-nav"
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          gap: 12,
          height: '100dvh',
          overflow: 'hidden',
        }}
      >
        {/* Tabs */}
        <div className="row" role="tablist" aria-label="Add friends method" style={{ gap: 6 }}>
          {TABS.map(({ id, label, icon }) => {
            const active = tab === id
            return (
              <button
                type="button"
                key={id}
                role="tab"
                aria-selected={active}
                aria-controls={`add-tab-${id}`}
                id={`add-tab-trigger-${id}`}
                onClick={() => setTab(id)}
                className="chip"
                data-testid={`add-tab-${id}`}
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  minHeight: 46,
                  gap: 6,
                  background: active ? 'var(--ink)' : 'var(--paper)',
                  color: active ? 'var(--paper)' : 'var(--ink)',
                }}
              >
                <Icon name={icon} size={14} />
                {label}
              </button>
            )
          })}
        </div>

        {bootError && (
          <div className="card body-sm" style={{ textAlign: 'center', color: 'var(--danger)' }}>
            {bootError}
          </div>
        )}

        {tab === 'qr' && (
          <QrTab
            inviteUrl={inviteUrl}
            friendCode={friendCode}
            onScanResult={(code) => redeemAndToast(code, showToast)}
          />
        )}

        {tab === 'link' && (
          <LinkTab inviteUrl={inviteUrl} friendCode={friendCode} onToast={showToast} />
        )}

        {tab === 'name' && me && <NameTab onToast={showToast} />}

        {toast && (
          <output
            className="card"
            data-testid="add-toast"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 'calc(110px + env(safe-area-inset-bottom))',
              maxWidth: 340,
              padding: '12px 18px',
              background: toast.kind === 'success' ? 'var(--lime)' : 'var(--danger)',
              color: toast.kind === 'success' ? 'var(--ink)' : 'var(--paper)',
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              borderRadius: 14,
              border: '2.5px solid var(--line)',
              boxShadow: '0 6px 0 var(--shadow-ink)',
              zIndex: 60,
              textAlign: 'center',
            }}
          >
            {toast.text}
          </output>
        )}
      </div>
    </div>
  )
}

// ───── QR tab ─────────────────────────────────────────────────

function QrTab({
  inviteUrl,
  friendCode,
  onScanResult,
}: {
  inviteUrl: string
  friendCode: string | null
  onScanResult: (code: string) => void
}) {
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [scanning, setScanning] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)

  async function onFilePicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setScanning(true)
    setScanError(null)
    try {
      const code = await decodeFriendCodeFromImage(file)
      if (!code) {
        setScanError("Couldn't read a Stumble code in that image.")
        return
      }
      onScanResult(code)
    } catch {
      setScanError("Couldn't read the image.")
    } finally {
      setScanning(false)
    }
  }

  return (
    // F6: flex column fills viewport — no more 50% yellow blank at the bottom.
    // Spacer between top content + scan-code action pushes the CTA to the
    // bottom of the panel.
    <div
      id="add-tab-qr"
      role="tabpanel"
      aria-labelledby="add-tab-trigger-qr"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: 12,
      }}
    >
      <div
        className="card"
        style={{
          background: 'var(--paper-warm)',
          display: 'grid',
          placeItems: 'center',
          gap: 12,
        }}
      >
        <span className="eyebrow">your code</span>
        {friendCode ? (
          <QrSvg text={inviteUrl} size={220} />
        ) : (
          <div
            aria-busy="true"
            style={{
              width: 220,
              height: 220,
              display: 'grid',
              placeItems: 'center',
              border: '3px solid var(--line)',
              borderRadius: 14,
              background: 'var(--paper)',
              fontFamily: 'var(--font-display)',
              fontSize: 14,
              color: 'var(--muted)',
            }}
          >
            loading…
          </div>
        )}
        <div
          style={{ fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: '0.02em' }}
          data-testid="add-friend-code"
        >
          {friendCode ?? '------'}
        </div>
        <p className="body-sm muted" style={{ textAlign: 'center', margin: 0 }}>
          Show this to a new friend.
        </p>
      </div>

      {/* Flex spacer — pushes the scan CTA to the bottom of the panel. */}
      <div style={{ flex: 1 }} />

      <button
        type="button"
        className="btn pink full"
        onClick={() => fileInput.current?.click()}
        disabled={scanning}
        data-testid="add-scan-btn"
      >
        <Icon name="camera" size={18} /> {scanning ? 'reading…' : 'scan a code'}
      </button>
      {scanError && (
        <p className="body-sm" style={{ textAlign: 'center', color: 'var(--danger)' }}>
          {scanError}
        </p>
      )}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFilePicked}
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  )
}

// ───── LINK tab ───────────────────────────────────────────────

function LinkTab({
  inviteUrl,
  friendCode,
  onToast,
}: {
  inviteUrl: string
  friendCode: string | null
  onToast: (t: Toast) => void
}) {
  const disabled = !friendCode

  async function copy() {
    if (!inviteUrl) return
    try {
      await navigator.clipboard.writeText(inviteUrl)
      onToast({ kind: 'success', text: 'link copied' })
    } catch {
      onToast({ kind: 'error', text: "couldn't copy link" })
    }
  }

  async function share() {
    if (!inviteUrl) return
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'Stumble with me',
          text: 'add me on Stumble',
          url: `https://${inviteUrl}`,
        })
      } catch {
        // user cancel or unsupported scheme — fall back to clipboard
        await copy()
      }
    } else {
      await copy()
    }
  }

  return (
    // F6: flex column fills viewport — link card on top, action row pinned bottom.
    <div
      id="add-tab-link"
      role="tabpanel"
      aria-labelledby="add-tab-trigger-link"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: 12,
      }}
    >
      <div className="card" style={{ background: 'var(--paper-warm)', display: 'grid', gap: 12 }}>
        <span className="eyebrow">your invite link</span>
        <div
          data-testid="add-invite-link"
          style={{
            padding: '16px 14px',
            border: '2.5px solid var(--line)',
            borderRadius: 12,
            background: 'var(--paper)',
            fontFamily: 'var(--font-mono)',
            fontSize: 15,
            fontWeight: 700,
            wordBreak: 'break-all',
            textAlign: 'center',
          }}
        >
          {inviteUrl || 'loading…'}
        </div>
        <p className="body-sm muted" style={{ textAlign: 'center', margin: 0 }}>
          Anyone with this link can ask to be friends.
        </p>
      </div>

      {/* Flex spacer — pushes the copy/share row to the bottom. */}
      <div style={{ flex: 1 }} />

      <div className="row" style={{ gap: 10 }}>
        <button
          type="button"
          className="btn secondary full"
          onClick={copy}
          disabled={disabled}
          data-testid="add-copy-btn"
        >
          <Icon name="check" size={18} /> copy
        </button>
        <button
          type="button"
          className="btn pink full"
          onClick={share}
          disabled={disabled}
          data-testid="add-share-btn"
        >
          <Icon name="share" size={18} /> share
        </button>
      </div>
    </div>
  )
}

// ───── NAME tab ───────────────────────────────────────────────

function NameTab({ onToast }: { onToast: (t: Toast) => void }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<UserSearchHit[]>([])
  const [searching, setSearching] = useState(false)
  // Track which user IDs we've already sent requests to in this session so the
  // button reflects state without a refetch.
  const [requested, setRequested] = useState<Set<string>>(new Set())
  const [requesting, setRequesting] = useState<Set<string>>(new Set())

  // Debounced search — 300ms after last keystroke.
  useEffect(() => {
    const q = query.trim()
    if (!q) {
      setResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    const handle = window.setTimeout(async () => {
      const hits = await friendsApi.search(q)
      setResults(hits)
      setSearching(false)
    }, 300)
    return () => window.clearTimeout(handle)
  }, [query])

  async function addUser(hit: UserSearchHit) {
    if (hit.relationship !== 'none') return
    const id = hit.user.id
    setRequesting((prev) => new Set(prev).add(id))
    try {
      await apiFetch('/api/friends/requests', {
        method: 'POST',
        body: JSON.stringify({ addresseeId: id }),
      })
      setRequested((prev) => new Set(prev).add(id))
      onToast({ kind: 'success', text: 'request sent' })
    } catch {
      onToast({ kind: 'error', text: "couldn't send request" })
    } finally {
      setRequesting((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  return (
    // F6: flex column fills viewport — search + results on top, REDEEM strip
    // pinned bottom. The results region scrolls internally when many matches.
    <div
      id="add-tab-name"
      role="tabpanel"
      aria-labelledby="add-tab-trigger-name"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: 12,
      }}
    >
      <input
        type="search"
        inputMode="search"
        enterKeyHint="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="@username or name"
        aria-label="Search by username or name"
        data-testid="add-search-input"
        style={{
          minHeight: 52,
          padding: '0 14px',
          border: '2.5px solid var(--line)',
          borderRadius: 12,
          background: 'var(--paper)',
          fontWeight: 800,
          fontSize: 16,
          boxShadow: '0 5px 0 var(--shadow-ink)',
        }}
      />

      <div
        data-testid="add-search-results"
        style={{ display: 'grid', gap: 8, flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        {query.trim() === '' && (
          <p className="body-sm muted" style={{ textAlign: 'center', padding: 8, margin: 0 }}>
            Type a name to start searching.
          </p>
        )}
        {query.trim() !== '' && searching && (
          <p className="body-sm muted" style={{ textAlign: 'center', padding: 8, margin: 0 }}>
            searching…
          </p>
        )}
        {query.trim() !== '' && !searching && results.length === 0 && (
          <p className="body-sm muted" style={{ textAlign: 'center', padding: 8, margin: 0 }}>
            No match. Try the LINK tab — easiest with new humans.
          </p>
        )}
        {results.map((hit) => (
          <SearchResultRow
            key={hit.user.id}
            hit={hit}
            requested={requested.has(hit.user.id)}
            requesting={requesting.has(hit.user.id)}
            onAdd={() => addUser(hit)}
          />
        ))}
      </div>

      <RedeemStrip onToast={onToast} />
    </div>
  )
}

const AVATAR_COLORS = [
  'var(--pink)',
  'var(--lime)',
  'var(--teal)',
  'var(--orange)',
  'var(--plum)',
  'var(--acid)',
] as const

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]
}

function SearchResultRow({
  hit,
  requested,
  requesting,
  onAdd,
}: {
  hit: UserSearchHit
  requested: boolean
  requesting: boolean
  onAdd: () => void
}) {
  const { user, relationship } = hit
  const letter = (user.displayName.trim().charAt(0) || '?').toUpperCase()
  const color = avatarColor(user.friendCode ?? user.id)

  let label: string
  let cls: string
  let disabled = true
  if (relationship === 'self') {
    label = 'you'
    cls = 'chip'
  } else if (relationship === 'friends') {
    label = 'friends'
    cls = 'chip solid-lime'
  } else if (relationship === 'outgoing_pending' || requested) {
    label = 'request sent'
    cls = 'chip'
  } else if (relationship === 'incoming_pending') {
    label = 'accept'
    cls = 'chip solid-lime'
    disabled = false
  } else {
    label = requesting ? '…' : '+ add'
    cls = 'chip solid-pink'
    disabled = requesting
  }

  return (
    <div
      className="row-between"
      data-testid={`add-result-${user.id}`}
      style={{
        padding: '10px 12px',
        border: '2.5px solid var(--line)',
        borderRadius: 14,
        background: 'var(--paper)',
        boxShadow: '0 4px 0 var(--shadow-ink)',
      }}
    >
      <div className="row" style={{ gap: 12, minWidth: 0 }}>
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 999,
            background: color,
            color: 'var(--paper)',
            border: '2px solid var(--line)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-display)',
            fontSize: 18,
            flexShrink: 0,
          }}
          aria-hidden
        >
          {letter}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              lineHeight: 1,
              textTransform: 'lowercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user.displayName}
          </div>
          {user.username && (
            <div className="body-sm muted" style={{ fontSize: 12 }}>
              @{user.username}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        className={cls}
        onClick={onAdd}
        disabled={disabled}
        data-testid={`add-result-btn-${user.id}`}
        style={{ minHeight: 36, whiteSpace: 'nowrap' }}
      >
        {label}
      </button>
    </div>
  )
}

// ───── Redeem strip ───────────────────────────────────────────

function RedeemStrip({ onToast }: { onToast: (t: Toast) => void }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  async function redeem() {
    const clean = code.trim()
    if (!clean) return
    setBusy(true)
    try {
      await redeemFriendCode(clean)
      setCode('')
      onToast({ kind: 'success', text: 'friend added!' })
    } catch (err) {
      if (err instanceof StumbleApiError && err.status === 404) {
        onToast({ kind: 'error', text: 'code not found' })
      } else {
        onToast({ kind: 'error', text: "couldn't redeem code" })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="card"
      style={{
        display: 'grid',
        gap: 10,
        background: 'var(--paper-warm)',
        marginTop: 4,
      }}
    >
      <span className="eyebrow">redeem a code</span>
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={code}
          onChange={(e) =>
            setCode(
              e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .slice(0, 8),
            )
          }
          placeholder="e.g. ALICE01"
          aria-label="Friend code"
          data-testid="add-redeem-input"
          style={{
            flex: 1,
            minHeight: 48,
            padding: '0 14px',
            border: '2.5px solid var(--line)',
            borderRadius: 12,
            background: 'var(--paper)',
            fontFamily: 'var(--font-mono)',
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            boxShadow: '0 4px 0 var(--shadow-ink)',
          }}
        />
        <button
          type="button"
          className="btn pink"
          onClick={redeem}
          disabled={busy || code.trim().length === 0}
          data-testid="add-redeem-btn"
        >
          {busy ? '…' : 'redeem'}
        </button>
      </div>
    </div>
  )
}

// ───── QR encode (inline SVG) ────────────────────────────────

function QrSvg({ text, size }: { text: string; size: number }) {
  const cells = useMemo(() => buildQrCells(text), [text])
  if (!cells) return null
  const moduleCount = cells.length
  const cell = Math.floor(size / moduleCount)
  const dim = cell * moduleCount
  const rects: { x: number; y: number }[] = []
  for (let y = 0; y < moduleCount; y++) {
    for (let x = 0; x < moduleCount; x++) {
      if (cells[y]?.[x]) rects.push({ x: x * cell, y: y * cell })
    }
  }
  const style: CSSProperties = {
    background: 'var(--paper)',
    border: '3px solid var(--line)',
    borderRadius: 14,
    padding: 8,
  }
  return (
    <svg
      width={dim}
      height={dim}
      viewBox={`0 0 ${dim} ${dim}`}
      style={style}
      role="img"
      aria-label="Your friend code QR"
    >
      <title>Stumble friend code</title>
      {rects.map(({ x, y }) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={cell} height={cell} fill="var(--ink)" />
      ))}
    </svg>
  )
}

function buildQrCells(text: string): boolean[][] | null {
  try {
    const q = qrcode(0, 'M')
    q.addData(text)
    q.make()
    const n = q.getModuleCount()
    const rows: boolean[][] = []
    for (let y = 0; y < n; y++) {
      const row: boolean[] = []
      for (let x = 0; x < n; x++) row.push(q.isDark(y, x))
      rows.push(row)
    }
    return rows
  } catch {
    return null
  }
}

// ───── QR decode + redeem ────────────────────────────────────

async function decodeFriendCodeFromImage(file: File): Promise<string | null> {
  // Try BarcodeDetector first (Android Chrome, some desktops); fall back to jsqr.
  const url = URL.createObjectURL(file)
  try {
    const detected = await tryBarcodeDetector(url)
    if (detected) return extractCodeFromText(detected)
    const raw = await decodeWithJsqr(url)
    return raw ? extractCodeFromText(raw) : null
  } finally {
    URL.revokeObjectURL(url)
  }
}

type MaybeBarcodeDetector = {
  new (opts: { formats: string[] }): {
    detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>
  }
}

async function tryBarcodeDetector(url: string): Promise<string | null> {
  const ctor = (
    globalThis as unknown as {
      BarcodeDetector?: MaybeBarcodeDetector
    }
  ).BarcodeDetector
  if (!ctor) return null
  try {
    const img = await loadImage(url)
    const detector = new ctor({ formats: ['qr_code'] })
    const found = await detector.detect(img)
    return found[0]?.rawValue ?? null
  } catch {
    return null
  }
}

async function decodeWithJsqr(url: string): Promise<string | null> {
  const img = await loadImage(url)
  const canvas = document.createElement('canvas')
  // Cap at 1024px to keep the jsqr pass quick on big phone photos.
  const max = 1024
  const scale = Math.min(1, max / Math.max(img.width, img.height))
  canvas.width = Math.max(1, Math.floor(img.width * scale))
  canvas.height = Math.max(1, Math.floor(img.height * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const { default: jsQR } = await import('jsqr')
  const out = jsQR(data.data, canvas.width, canvas.height)
  return out?.data ?? null
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image load failed'))
    img.src = url
  })
}

// Accept either a full invite URL (with or without scheme) or a bare code.
function extractCodeFromText(text: string): string | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  // Match {host}/f/{code} (allow http(s)://, www., subdomains).
  const m = trimmed.match(/\/f\/([A-Z0-9]+)/i)
  if (m?.[1]) return m[1].toUpperCase()
  // Bare alphanumeric code (2-12 chars) — accept directly.
  if (/^[A-Z0-9]{2,12}$/i.test(trimmed)) return trimmed.toUpperCase()
  return null
}

async function redeemFriendCode(code: string): Promise<void> {
  await apiFetch('/api/friends/code/redeem', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

async function redeemAndToast(code: string, toast: (t: Toast) => void): Promise<void> {
  try {
    await redeemFriendCode(code)
    toast({ kind: 'success', text: 'friend added!' })
  } catch (err) {
    if (err instanceof StumbleApiError && err.status === 404) {
      toast({ kind: 'error', text: 'code not found' })
    } else {
      toast({ kind: 'error', text: "couldn't redeem code" })
    }
  }
}
